import { BasePagination, Box, Input, InputGroup, InputLeftElement, Text, useDisclosure } from '@elkaso-app/web-design';
import { useGetSiProductsApi } from 'apis/supplier-intelligence';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import { BaseTableSpinner, BaseTableTitle } from 'components/base-table/components';
import { TableHeader } from 'components/base-table/components/table-header';
import { BaseSortButton } from 'components/base-table/sorting/base-sort-button';
import { useDebounce } from 'hooks/use-debounce';
import { usePageParams } from 'hooks/use-page-params';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { ProductIntelligenceDrawer } from '../components/product-intelligence-drawer';
import { SupplierNameFilterButton } from '../components/supplier-name-filter-button';

const ProductListScreen = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const setPaginationParams = useSetPaginationParamsV1();
  const { getPageParams, setPageParams } = usePageParams();
  const { search } = getPageParams();
  const [searchInput, setSearchInput] = useState((search as string) || '');
  const debouncedSearch = useDebounce(searchInput, 400);
  const { data: apiData, isLoading, isFetching } = useGetSiProductsApi();
  const { pageCount, total, data: productsData } = apiData ?? {};
  const tableData = useMemo(() => (productsData ?? []).map((item: any) => ({ ...item, id: item.productId })), [productsData]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedProductName, setSelectedProductName] = useState<string | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);

  useEffect(() => {
    dispatch(changeTitle('Supplier Intelligence — Products'));
  }, [dispatch]);

  useEffect(() => {
    const params = getPageParams();
    setPageParams({ ...params, search: debouncedSearch || undefined });
  }, [debouncedSearch]);

  const handleProductClick = useCallback(
    (productName: string, productIds: number[]) => {
      setSelectedProductName(productName);
      setSelectedProductIds(productIds);
      onOpen();
    },
    [onOpen]
  );

  const columns = useMemo(
    () => [
      {
        Header: <TableHeader name='Product Name' sortButtonComponent={<BaseSortButton sortBy='name' />} />,
        accessor: 'name',
        Cell: ({ value, row }: any) => (
          <Text
            color='blue.600'
            cursor='pointer'
            fontWeight='medium'
            _hover={{ textDecoration: 'underline' }}
            onClick={() => handleProductClick(row.original.name, row.original.productIds ?? [row.original.productId])
            }>
            {value}
          </Text>
        ),
      },
      {
        Header: 'Brands',
        accessor: 'brands',
        Cell: ({ value }: any) => <Text>{(value ?? []).join(', ') || '—'}</Text>,
      },
      { Header: 'Category', accessor: 'category' },
      {
        Header: (
          <TableHeader
            name='Suppliers'
            sortButtonComponent={<BaseSortButton sortBy='supplierName' />}
            filterButtonComponent={<SupplierNameFilterButton />}
          />
        ),
        accessor: 'suppliers',
        Cell: ({ value }: any) => {
          const suppliers = value ?? [];
          if (!suppliers.length) return <Text>—</Text>;
          return (
            <Text>
              {suppliers.map((s: any, i: number) => (
                <Text
                  key={s.id}
                  as='span'
                  color='blue.600'
                  cursor='pointer'
                  fontWeight='medium'
                  _hover={{ textDecoration: 'underline' }}
                  onClick={() => history.push(`${match.url.replace('/products', '')}/suppliers/${s.id}`)}>
                  {s.name}
                  {i < suppliers.length - 1 ? ', ' : ''}
                </Text>
              ))}
            </Text>
          );
        },
      },
      {
        Header: <TableHeader name='Min Price (AED)' sortButtonComponent={<BaseSortButton sortBy='minPrice' />} />,
        accessor: 'minPrice',
        Cell: ({ value }: any) => <Text>{value != null ? value.toFixed(2) : '—'}</Text>,
      },
      {
        Header: <TableHeader name='Max Price (AED)' sortButtonComponent={<BaseSortButton sortBy='maxPrice' />} />,
        accessor: 'maxPrice',
        Cell: ({ value }: any) => <Text>{value != null ? value.toFixed(2) : '—'}</Text>,
      },
      {
        Header: <TableHeader name='Total Orders' sortButtonComponent={<BaseSortButton sortBy='totalOrders' />} />,
        accessor: 'totalOrders',
      },
      { Header: 'Restaurants', accessor: 'restaurantsOrdering' },
    ],
    [handleProductClick, history, match.url]
  );

  return (
    <>
      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <BaseTableTitle title='Products' />
            <BaseTableSpinner isFetching={isFetching} />
          </BaseTableContainer.Header.Left>
        </BaseTableContainer.Header>

        <Box px={6} py={4}>
          <InputGroup maxW='400px'>
            <InputLeftElement pointerEvents='none'>
              <FiSearch color='gray' />
            </InputLeftElement>
            <Input
              placeholder='Search by product name...'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>
        </Box>

        <BaseTableContainer.TableWrapper>
          <BaseTable columns={columns} data={tableData} />
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>

      <ProductIntelligenceDrawer isOpen={isOpen} onClose={onClose} productName={selectedProductName} productIds={selectedProductIds} />
    </>
  );
};

export default ProductListScreen;
